import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';

import { DatePicker, Dropdown, ElasticFormModal, Fetching, FormRow, Label, TimeField } from 'basic-components';
import { DataStorageEnums, GetDataBody, Key } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import TimeService from 'dashboard-services/services/TimeService';

import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';
import FORMULAS from 'resources/constants/Formulas.js';
import { ExcelUtils, FormulaUtils, GAUtils, TimeSeriesUtils } from 'utils';

import NoColumnsWarning from './NoColumnsWarning';

import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { Else, If, Then, When } from 'react-if';

import './AddSeriesDatapointModal.scss';

const EMPTY_ARRAY = []
const DATA_GRANULARITY = "DataGranularity"

// TODO: Fetch those values from NGPCD NG Metadata Standards when available later
const DATA_GRANULARITY_VALUES = {
  HOURLY: "Hourly",
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  ANNUALLY: "Annually"
}

dayjs.extend(utc)
const getIntersectingColumns = symbols => {
  if (!Array.isArray(symbols) || symbols.length === 0) {
    return EMPTY_ARRAY
  }
  return symbols.reduce((common, item, index) => {
    if (!Array.isArray(item.values)) {
      return common
    }
    if (index === 0) {
      return [...item.values]
    }

    return common.filter(val => item.values.includes(val))
  },[])
}

const determineDataGranularity = symbols => {
  if (!Array.isArray(symbols) || symbols.length === 0) {
    return undefined
  }
  const dataGranularityArray = symbols.map(symbol => symbol?.metadata?.[DATA_GRANULARITY])
  if (dataGranularityArray.length === 0) {
    return undefined
  }
  const areAllGranularitiesEqual = dataGranularityArray.every(val => val === dataGranularityArray[0])
  return areAllGranularitiesEqual ? dataGranularityArray[0] : undefined
}

const getDefaultDate = ({ dataGranularity, definedTime }) => {
  dayjs.extend(quarterOfYear)
  dayjs.extend(timezone)
  const currentDate = TimeService.getCurrentDate(definedTime)
  const monthStartDate = dayjs.tz(currentDate, definedTime).startOf("month").format(TimeService.ISO_DATE_FORMAT)
  const quarterStartDate = dayjs.tz(currentDate, definedTime).startOf("quarter").format(TimeService.ISO_DATE_FORMAT)
  const yearStartDate = dayjs.tz(currentDate, definedTime).startOf("year").format(TimeService.ISO_DATE_FORMAT)
  switch (dataGranularity) {
    default:
    case DATA_GRANULARITY_VALUES.HOURLY:
    case DATA_GRANULARITY_VALUES.DAILY:
      return currentDate
    case DATA_GRANULARITY_VALUES.WEEKLY:
      return undefined
    case DATA_GRANULARITY_VALUES.MONTHLY:
      return monthStartDate
    case DATA_GRANULARITY_VALUES.QUARTERLY:
      return quarterStartDate
    case DATA_GRANULARITY_VALUES.ANNUALLY:
      return yearStartDate
  }
}

const useInsertSeriesDatapoint = ({ datapointSymbol, onClose, selectedColumn, selectedSymbols, source, dateTimeUTCForFormula }) => (
  useCallback(async () => {
    GAUtils.sendEvent({
      category: "action",
      action: "add_ts",
      label: "add"
    })
    const address = await ExcelUtils.getSelectedCellAddress()
    if(datapointSymbol?.symbols) {
      const stringifiedSymbols = FormulaUtils.stringifyExplicitSymbolsForFormula(datapointSymbol.symbols)
      ExcelUtils.addToFormulaCell({ address, formula: `=${FORMULAS.LOAD_TS_DATAPOINT}("${dateTimeUTCForFormula}", "${stringifiedSymbols}", "${datapointSymbol.groupName}", "${selectedColumn}", "${source}")` })
      onClose()
      return
    }
    if(selectedSymbols?.length) {
      const formulas = selectedSymbols.map(symbol => {
        const stringifiedSymbols = FormulaUtils.stringifyExplicitSymbolsForFormula(symbol.symbols)
        return `=${FORMULAS.LOAD_TS_DATAPOINT}("${dateTimeUTCForFormula}", "${stringifiedSymbols}", "${symbol.groupName}", "${selectedColumn}", "${source}")`
      })
      ExcelUtils.addFormulasBelow({ startAddress: address, formulas })
      onClose()
    }
  }, [datapointSymbol.groupName, datapointSymbol.symbols, dateTimeUTCForFormula, onClose, selectedColumn, selectedSymbols, source])
)

const getLastDatapointRequestBody = ({ datapointSymbol, selectedSymbols, selectedColumn, source }) => {
  const baseSymbol = Object.keys(datapointSymbol).length > 0 ? datapointSymbol : selectedSymbols[0]
  if(!selectedColumn || !baseSymbol?.groupName) {
    return undefined
  }

  let builder = new GetDataBody.Builder()
  const keyBuilder = new Key.Builder()
    .withColumns([selectedColumn])
    .withGroupName(baseSymbol.groupName)
    .withSymbols(baseSymbol.symbols)
    .withMetadatas(baseSymbol.metadata)

  builder = builder.withKey(keyBuilder.build().serialize())
  builder = builder
    .withLastType(DataStorageEnums.LAST_TYPE.VALUE)
    .withLastTypeAmount(2)
    .withRange(DataStorageEnums.DATA_RANGE.LAST)
    .withTimeZone(TimeService.UTC)
    .withSource(source)

  return builder.build().serialize()
}

const AddSeriesDatapointModal = ({ onClose }) => {
  const dispatch = useDispatch(),
        definedTime = useSelector(state => state.settingsState.definedTime || undefined),
        localSource = useSelector(state => state.globalState.localSource),
        [selectedColumn, setSelectedColumn] = useState(undefined),
        datapointSymbol = useSelector(state => state.seriesState.creatorState.datapointSymbol),
        selectedSymbols = useSelector(state => state.seriesState.creatorState.selectedSymbols),
        dataGranularity =  datapointSymbol?.metadata?.[DATA_GRANULARITY] || determineDataGranularity(selectedSymbols),
        [date, setDate] = useState(getDefaultDate({ dataGranularity, definedTime })),
        [time, setTime] = useState(dataGranularity === DATA_GRANULARITY_VALUES.HOURLY ? undefined : TimeService.getUserFormattedTime(date, definedTime)),
        dateTimeUTC = useMemo(() => (dayjs(date, TimeService.ISO_DATE_FORMAT, true).isValid() && time) ? TimeService.getUTCDateTime(date + "T" + time, definedTime, TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined, [date, definedTime, time]),
        dateTimeUTCForFormula = useMemo(() => dateTimeUTC ? (dataGranularity === DATA_GRANULARITY_VALUES.HOURLY ? dateTimeUTC : dayjs.utc(dateTimeUTC).format(TimeService.ISO_DATE_FORMAT)) : undefined, [dataGranularity, dateTimeUTC]),
        selectedSymbolsAvailableColumns = useMemo(() => getIntersectingColumns(selectedSymbols), [selectedSymbols]),
        areThereAnyColumnsAvailable = useMemo(() => datapointSymbol?.values?.length > 0 || selectedSymbolsAvailableColumns.length > 0,[datapointSymbol?.values, selectedSymbolsAvailableColumns.length]),
        firstColumnFromArray = useMemo(() => datapointSymbol?.values?.[0] || selectedSymbolsAvailableColumns?.[0], [datapointSymbol?.values, selectedSymbolsAvailableColumns]),
        targetSource = useMemo(() => {
          if (datapointSymbol?.source) {
            return datapointSymbol.source
          } else if (selectedSymbols.length > 0 && selectedSymbols[0]?.source) {
            return selectedSymbols[0].source 
          } else {
            return localSource
          }
        }, [datapointSymbol, localSource, selectedSymbols]),
        insertDatapoint = useInsertSeriesDatapoint({ datapointSymbol, onClose, selectedColumn, selectedSymbols, source: targetSource, dateTimeUTCForFormula }),
        getTextQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withHeader("Target", targetSource, true)
            .withOnParse(response => response.text())
            .withShouldDisplayError(false)
        , [dispatch, targetSource]),
        submitProps = useMemo(() => ({
          disabled: !date || !dayjs(date, TimeService.ISO_DATE_FORMAT, true).isValid() || !time || !selectedColumn
        }), [date, selectedColumn, time]),
        requestBody = useMemo(() => getLastDatapointRequestBody({ datapointSymbol, selectedSymbols, selectedColumn: firstColumnFromArray, source: targetSource }), [datapointSymbol, firstColumnFromArray, selectedSymbols, targetSource]),
        onSelectLastDatapoint = useCallback(response => TimeSeriesUtils.getFirstAppearingDate(response), []),
        { data: lastDatapointDateTimeUTC, isLoading } = dataStorageQueries.useGetStreamData({
          getQueryBuilder: getTextQueryBuilder,
          body: requestBody,
          size: 1,
          enabled: !!requestBody,
          select: onSelectLastDatapoint
        }),
        userFormattedLastDatapointDateTime = useMemo(() => TimeService.getUserFormattedDateTime(lastDatapointDateTimeUTC, definedTime),[definedTime, lastDatapointDateTimeUTC])

  useEffect(() => {
    if (firstColumnFromArray) {
      setSelectedColumn(firstColumnFromArray)
    }
  }, [datapointSymbol.values, firstColumnFromArray, selectedSymbolsAvailableColumns])

  useEffect(() => {
    if (userFormattedLastDatapointDateTime) {
      setDate(dayjs.tz(userFormattedLastDatapointDateTime, definedTime).format(TimeService.ISO_DATE_FORMAT))
    }
  }, [definedTime, userFormattedLastDatapointDateTime])

  return (
    <ElasticFormModal
        className="ng-office-app__authed__content__body__item__datapoint-modal"
        onClose={onClose}
        onSubmit={insertDatapoint}
        open
        submitProps={submitProps}
        submitText="Add to sheet"
        title="Add single data point"
        withFocusTrap
    >
      <Label
          className="ng-office-app__authed__content__body__item__datapoint-modal__label"
          required
          size="large"
      >
        Data point date and time
      </Label>
      <FormRow className="ng-office-app__authed__content__body__item__datapoint-modal__datetime-container">
        <DatePicker
            defaultValue={date}
            name="date"
            onChange={setDate}
            variant="border"
        />
        <TimeField
            defaultValue={time}
            name="time"
            onChange={setTime}
            variant="border"
            withSeconds
        />
      </FormRow>
      <Label 
          className="ng-office-app__authed__content__body__item__datapoint-modal__label"
          required
      >
        Column
      </Label>
      <div className="ng-office-app__authed__content__body__item__datapoint-modal__columns-container">
        <Dropdown
            creatable
            defaultValue={selectedColumn}
            disabled={!areThereAnyColumnsAvailable}
            fixed
            labelId="column"
            name="selectedColumns"
            noError
            onChange={setSelectedColumn}
            options={datapointSymbol?.values ? datapointSymbol.values : selectedSymbolsAvailableColumns}
            placeholder="Columns"
            variant="border"
        />
      </div>
      <div className="ng-office-app__authed__content__body__item__datapoint-modal__data-granularity">
        <If condition={isLoading}>
          <Then>
            {() => (
              <Fetching loading={isLoading}/>
            )}
          </Then>
          <Else>
            {() => (
              <Fragment>
                <div>
                  <span className="ng-office-app__authed__content__body__item__datapoint-modal__data-granularity--muted-text">
                    {dataGranularity ? "Data frequency: " : "Data frequency not found."}
                  </span>
                  {dataGranularity ? dataGranularity : ""}
                </div>
                <div>
                  <span className="ng-office-app__authed__content__body__item__datapoint-modal__data-granularity--muted-text">
                    {lastDatapointDateTimeUTC ? "Last data point: " : "Last data point not found."}
                  </span>
                  {lastDatapointDateTimeUTC ? userFormattedLastDatapointDateTime : ""}
                </div>
              </Fragment>
            )}
          </Else>
        </If>
      </div>
      <When condition={!areThereAnyColumnsAvailable}>
        <NoColumnsWarning/>
      </When>
    </ElasticFormModal>
  )
}

AddSeriesDatapointModal.propTypes = {
  onClose: propTypes.func.isRequired
}

export default AddSeriesDatapointModal